import { Injectable } from '@angular/core';
import { of } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiSeo } from 'idt-ecommerce-api/_api/api-web/api-seo/api-seo.models';
import { ApiSeoService } from 'idt-ecommerce-api/_api/api-web/api-seo/api-seo.service';
import { RenderSetupDataService } from 'idt-ecommerce-api/render-setup-data/render-setup-data.service';

import { Seo } from './seo.models';
import { SeoModelsTransformer } from './seo.models.transformer';


@Injectable()
export class SeoService {

  constructor(
    private readonly apiSeoService: ApiSeoService,
    private readonly renderSetupDataService: RenderSetupDataService
  ) { }

  /**
   * Returns the SEO config for the tenant store.
   *
   * Resolution order:
   * 1. RenderSetupData — injected via `launch({ setupData })` by the webcomponent host.
   * 2. sessionStorage['iSeoConfigJSON'] — pre-populated by the webcomponent host to avoid an HTTP
   *    roundtrip (see CMS-7069). Supports both the current and the legacy domain shapes transparently.
   * 3. HTTP fallback — fetches from the e-commerce API and transforms the response.
   */
  public seo() {
    const fromSetupData = SeoModelsTransformer.seoFromDomainInput(this.renderSetupDataService.seo);
    if (fromSetupData) {
      return of(fromSetupData);
    }

    const rawCached = sessionStorage.getItem('iSeoConfigJSON');
    if (rawCached) {
      // Wrapped in try/catch because `iSeoConfigJSON` is written by external hosts and may contain
      // invalid JSON. On error we fall through to the HTTP fetch
      // instead of breaking the observable chain.
      try {
        const fromCache = SeoModelsTransformer.seoFromDomainInput(JSON.parse(rawCached));
        if (fromCache) {
          return of(fromCache);
        }
      } catch { /* fall through to HTTP */ }
    }

    return this.apiSeoService.seo().pipe(
      map((apiSeo: ApiSeo) => {
        const seo: Seo = SeoModelsTransformer.seoFromApi(apiSeo);
        return seo;
      })
    );
  }

}
